import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loading from '../../components/common/Loading';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import axios from 'axios';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const propTypes = {
  user: PropTypes.object.isRequired,
  retailer: PropTypes.object.isRequired
};

function StacklineDataAdmin({ user, retailer }) {
  const [clearDevCacheButtonText, setClearDevCacheButtonText] = useState('Clear Dev Cache');
  const [clearProdCacheButtonText, setClearProdCacheButtonText] = useState('Clear Prod Cache');
  const [enableSuperUserButtonText, setEnableSuperUserButtonText] = useState('Enable Super User');
  const [disableSuperUserButtonText, setDisableSuperUserButtonText] = useState('Disable Super User');
  const [releaseAtlasButtonText, setReleaseAtlasButtonText] = useState('Release Atlas Sales');
  const [loadPublishIdDevButtonText, setLoadPublishIdDevButtonText] = useState('Load Dev Publish IDs');
  const [loadPublishIdProdButtonText, setLoadPublishIdProdButtonText] = useState('Load Prod Publish IDs');
  const [rollbackPublishIdDev, setRollbackPublishIdDev] = useState('');
  const [rollbackPublishIdProd, setRollbackPublishIdProd] = useState('');
  const [revertPublishIdButtonDevText, setRevertPublishIdButtonDevText] = useState('Revert Dev to ID');
  const [revertPublishIdButtonProdText, setRevertPublishIdButtonProdText] = useState('Revert Prod to ID');
  const [publishIdsDev, setPublishIdsDev] = useState([]);
  const [publishIdsProd, setPublishIdsProd] = useState([]);
  const [disableRevertDevButton, setDisableRevertDevButton] = useState(true);
  const [disableRevertProdButton, setDisableRevertProdButton] = useState(true);
  const [currentPublishIdForDevText, setCurrentPublishIdForDevText] = useState('');
  const [currentPublishIdForProdText, setCurrentPublishIdForProdText] = useState('');
  const [revertStage, setRevertStage] = useState('');
  const [releaseDorisButtonText, setReleaseDorisButtonText] = useState('Release Doris Atlas Sales Data');
  const [loadDorisPublishIdProdButtonText, setLoadDorisPublishIdProdButtonText] = useState('Load Doris Prod Publish IDs');
  const [dorisPublishIdsProd, setDorisPublishIdsProd] = useState([]);
  const [rollbackDorisPublishIdProd, setRollbackDorisPublishIdProd] = useState('');
  const [revertDorisPublishIdButtonProdText, setRevertDorisPublishIdButtonProdText] = useState('Revert Doris Prod to ID');
  const [disableRevertDorisProdButton, setDisableRevertDorisProdButton] = useState(true);
  const [currentDorisPublishIdForProdText, setCurrentDorisPublishIdForProdText] = useState('');
  const [loadDorisPublishIdDevButtonText, setLoadDorisPublishIdDevButtonText] = useState('Load Doris Dev Publish IDs');
  const [dorisPublishIdsDev, setDorisPublishIdsDev] = useState([]);
  const [rollbackDorisPublishIdDev, setRollbackDorisPublishIdDev] = useState('');
  const [revertDorisPublishIdButtonDevText, setRevertDorisPublishIdButtonDevText] = useState('Revert Doris Dev to ID');
  const [disableRevertDorisDevButton, setDisableRevertDorisDevButton] = useState(true);
  const [currentDorisPublishIdForDevText, setCurrentDorisPublishIdForDevText] = useState('');
  const [dorisPromotionsPublishIdsProd, setDorisPromotionsPublishIdsProd] = useState([]);
  const [rollbackDorisPromotionsPublishIdProd, setRollbackDorisPromotionsPublishIdProd] = useState('');
  const [revertDorisPromotionsPublishIdButtonProdText, setRevertDorisPromotionsPublishIdButtonProdText] = useState('Revert Doris Prod to ID');
  const [disableRevertDorisPromotionsProdButton, setDisableRevertDorisPromotionsProdButton] = useState(true);
  const [currentDorisPromotionsPublishIdForProdText, setCurrentDorisPromotionsPublishIdForProdText] = useState('');
  const [dorisPromotionsPublishIdsDev, setDorisPromotionsPublishIdsDev] = useState([]);
  const [rollbackDorisPromotionsPublishIdDev, setRollbackDorisPromotionsPublishIdDev] = useState('');
  const [revertDorisPromotionsPublishIdButtonDevText, setRevertDorisPromotionsPublishIdButtonDevText] = useState('Revert Doris Dev to ID');
  const [disableRevertDorisPromotionsDevButton, setDisableRevertDorisPromotionsDevButton] = useState(true);
  const [currentDorisPromotionsPublishIdForDevText, setCurrentDorisPromotionsPublishIdForDevText] = useState('');
  const [dorisPublishingPaused, setDorisPublishingPaused] = useState(false);
  const [dorisPublishingUpdatedBy, setDorisPublishingUpdatedBy] = useState('');
  const [dorisPublishingUpdatedAt, setDorisPublishingUpdatedAt] = useState(0);
  const [dorisPublishingLoading, setDorisPublishingLoading] = useState(true);
  const [dorisPublishingToggling, setDorisPublishingToggling] = useState(false);

  const buttonClassName = `reclassify__button sl-delete-button sl-delete-button--sm`;

  const fetchDorisPublishingStatus = async () => {
    try {
      const res = await axios.get('/api/admin/GetDorisPublishingStatus');
      setDorisPublishingPaused(res.data.isPaused);
      setDorisPublishingUpdatedBy(res.data.updatedBy || '');
      setDorisPublishingUpdatedAt(res.data.updatedAt || 0);
    } catch (error) {
      console.error('Error fetching Doris publishing status:', error);
    } finally {
      setDorisPublishingLoading(false);
    }
  };

  useEffect(() => {
    fetchDorisPublishingStatus();
  }, []);

  const handleDorisPublishingToggle = async () => {
    const newPausedState = !dorisPublishingPaused;
    const action = newPausedState ? 'pause' : 'resume';
    if (!window.confirm(`Are you sure you want to ${action} Doris publishing?`)) {
      return;
    }
    setDorisPublishingToggling(true);
    try {
      const res = await axios.post(`/api/admin/SetDorisPublishingPaused?isPaused=${newPausedState}`);
      setDorisPublishingPaused(res.data.isPaused);
      setDorisPublishingUpdatedBy(res.data.updatedBy || '');
      setDorisPublishingUpdatedAt(res.data.updatedAt || 0);
    } catch (error) {
      console.error(`Error ${action}ing Doris publishing:`, error);
    } finally {
      setDorisPublishingToggling(false);
    }
  };

  const handleClearDevCacheClick = async () => {
    setClearDevCacheButtonText('processing...');
    await axios.post('/api/admin/ClearCacheByStage?stage=dev');
    setClearDevCacheButtonText('Done!');
  };

  const handleClearProdCacheClick = async () => {
    setClearProdCacheButtonText('processing...');
    await axios.post('/api/admin/ClearCacheByStage?stage=prod');
    setClearProdCacheButtonText('Done!');
  };

  const handleReleaseAtlasSalesClick = async () => {
    setReleaseAtlasButtonText('processing...');
    await axios.post(`/api/admin/ReleaseAtlasSales?retailerId=${retailer.id}`);
    setReleaseAtlasButtonText('Done!');
  };

  const handleEnableSuperUserClick = async () => {
    setEnableSuperUserButtonText('processing...');
    await axios.post('/api/admin/EnableAtlasSuperUser');
    setEnableSuperUserButtonText('Done!');
  };

  const handleDisableSuperUserClick = async () => {
    setDisableSuperUserButtonText('processing...');
    await axios.post('/api/admin/DisableAtlasSuperUser');
    setDisableSuperUserButtonText('Done!');
  };

  const handleLoadPublishIdDevClick = async () => {
    setLoadPublishIdDevButtonText('processing...');
    const res = await axios.post(`/api/admin/GetAtlasSalesPublishIds?retailerId=${retailer.id}&stage=dev`);
    setPublishIdsDev(res.data.past);
    setRevertStage('dev');
    setCurrentPublishIdForDevText(`Current publish ID for stage dev is ${res.data.current}.`);
    setLoadPublishIdDevButtonText('Load Dev Publish IDs');
  };

  const handleLoadPublishIdProdClick = async () => {
    setLoadPublishIdProdButtonText('processing...');
    const res = await axios.post(`/api/admin/GetAtlasSalesPublishIds?retailerId=${retailer.id}&stage=prod`);
    setPublishIdsProd(res.data.past);
    setRevertStage('prod');
    setCurrentPublishIdForProdText(`Current publish ID for stage prod is ${res.data.current}.`);
    setLoadPublishIdProdButtonText('Load Prod Publish IDs');
  };

  const handleRevertSalesToPublishIdDevClick = async () => {
    setRevertPublishIdButtonDevText('processing...');
    if (revertStage === 'prod' || revertStage === 'dev') {
      await axios.post(
        `/api/admin/RevertAtlasSalesToPublishId?retailerId=${retailer.id}&stage=dev&publishId=${rollbackPublishIdDev}`
      );
      setRevertPublishIdButtonDevText('Done!');
    }
  };

  const handleRevertSalesToPublishIdProdClick = async () => {
    setRevertPublishIdButtonProdText('processing...');
    if (revertStage === 'prod' || revertStage === 'dev') {
      await axios.post(
        `/api/admin/RevertAtlasSalesToPublishId?retailerId=${retailer.id}&stage=prod&publishId=${rollbackPublishIdProd}`
      );
      setRevertPublishIdButtonProdText('Done!');
    }
  };

  const handleReleaseDorisAtlasSalesDataClick = async () => {
    setReleaseDorisButtonText('processing...');
    try {
      const response = await axios.post('/api/atlas/ReleaseSalesData', {
        retailerIds: [retailer.id]
      });
      
      if (response.data.success === false) {
        console.error('Error releasing Doris Atlas sales data:', response.data);
        setReleaseDorisButtonText('Error occurred!');
        return;
      }
      
      setReleaseDorisButtonText('Done!');
    } catch (error) {
      console.error('Error releasing Doris Atlas sales data:', error);
      setReleaseDorisButtonText('Error occurred!');
    }
  };

  const handleLoadDorisPublishIdProdClick = async () => {
    setLoadDorisPublishIdProdButtonText('processing...');
    try {
      const res = await axios.get(`/api/atlas/loadPublishIdsProd?retailerId=${retailer.id}`);
      const publishIds = res.data.publishIds;
      
      // Sales data
      setDorisPublishIdsProd(publishIds.salesDataPublishIds || []);
      const currentSalesPublishId = publishIds.salesProduction?.publishId || 'N/A';
      setCurrentDorisPublishIdForProdText(`Current Doris sales publish ID for prod is ${currentSalesPublishId}.`);
      
      // Promotions data
      setDorisPromotionsPublishIdsProd(publishIds.promotionsDataPublishIds || []);
      const currentPromotionsPublishId = publishIds.promotionsProduction?.publishId || 'N/A';
      setCurrentDorisPromotionsPublishIdForProdText(`Current Doris promotions publish ID for prod is ${currentPromotionsPublishId}.`);
      
      setLoadDorisPublishIdProdButtonText('Load Doris Prod Publish IDs');
    } catch (error) {
      console.error('Error loading Doris publish IDs:', error);
      setLoadDorisPublishIdProdButtonText('Error occurred!');
    }
  };

  const handleRevertDorisToPublishIdProdClick = async () => {
    setRevertDorisPublishIdButtonProdText('processing...');
    try {
      await axios.post('/api/atlas/RevertSalesPublishId', {
        retailerId: retailer.id,
        publishId: rollbackDorisPublishIdProd,
        stage: 'prod'
      });
      setRevertDorisPublishIdButtonProdText('Done!');
    } catch (error) {
      console.error('Error reverting Doris to publish ID:', error);
      setRevertDorisPublishIdButtonProdText('Error occurred!');
    }
  };

  const handleLoadDorisPublishIdDevClick = async () => {
    setLoadDorisPublishIdDevButtonText('processing...');
    try {
      const res = await axios.get(`/api/atlas/loadPublishIdsDev?retailerId=${retailer.id}`);
      const publishIds = res.data.publishIds;
      
      // Sales data
      setDorisPublishIdsDev(publishIds.salesDataPublishIds || []);
      const currentSalesPublishId = publishIds.salesLatest?.publishId || 'N/A';
      setCurrentDorisPublishIdForDevText(`Current Doris sales publish ID for dev is ${currentSalesPublishId}.`);
      
      // Promotions data
      setDorisPromotionsPublishIdsDev(publishIds.promotionsDataPublishIds || []);
      const currentPromotionsPublishId = publishIds.promotionsLatest?.publishId || 'N/A';
      setCurrentDorisPromotionsPublishIdForDevText(`Current Doris promotions publish ID for dev is ${currentPromotionsPublishId}.`);
      
      setLoadDorisPublishIdDevButtonText('Load Doris Dev Publish IDs');
    } catch (error) {
      console.error('Error loading Doris dev publish IDs:', error);
      setLoadDorisPublishIdDevButtonText('Error occurred!');
    }
  };

  const handleRevertDorisToPublishIdDevClick = async () => {
    setRevertDorisPublishIdButtonDevText('processing...');
    try {
      await axios.post('/api/atlas/RevertSalesPublishId', {
        retailerId: retailer.id,
        publishId: rollbackDorisPublishIdDev,
        stage: 'dev'
      });
      setRevertDorisPublishIdButtonDevText('Done!');
    } catch (error) {
      console.error('Error reverting Doris dev to publish ID:', error);
      setRevertDorisPublishIdButtonDevText('Error occurred!');
    }
  };

  const handleRevertDorisPromotionsToPublishIdProdClick = async () => {
    setRevertDorisPromotionsPublishIdButtonProdText('processing...');
    try {
      await axios.post('/api/atlas/RevertPromotionsPublishId', {
        retailerId: retailer.id,
        publishId: rollbackDorisPromotionsPublishIdProd,
        stage: 'prod'
      });
      setRevertDorisPromotionsPublishIdButtonProdText('Done!');
    } catch (error) {
      console.error('Error reverting Doris promotions prod to publish ID:', error);
      setRevertDorisPromotionsPublishIdButtonProdText('Error occurred!');
    }
  };

  const handleRevertDorisPromotionsToPublishIdDevClick = async () => {
    setRevertDorisPromotionsPublishIdButtonDevText('processing...');
    try {
      await axios.post('/api/atlas/RevertPromotionsPublishId', {
        retailerId: retailer.id,
        publishId: rollbackDorisPromotionsPublishIdDev,
        stage: 'dev'
      });
      setRevertDorisPromotionsPublishIdButtonDevText('Done!');
    } catch (error) {
      console.error('Error reverting Doris promotions dev to publish ID:', error);
      setRevertDorisPromotionsPublishIdButtonDevText('Error occurred!');
    }
  };

  if (!user) {
    return <Loading className="spinner" />;
  }

  return (
    <div className="team">
      <h4 className="sl-header-text">Clear Cache</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleClearDevCacheClick();
        }}
      >
        {clearDevCacheButtonText}
      </Button>
      <br />
      <br />
      <br />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleClearProdCacheClick();
        }}
      >
        {clearProdCacheButtonText}
      </Button>
      <br />
      <br />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleEnableSuperUserClick();
        }}
      >
        {enableSuperUserButtonText}
      </Button>
      <br />
      <br />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleDisableSuperUserClick();
        }}
      >
        {disableSuperUserButtonText}
      </Button>
      <br />
      <br />
      <h4 className="sl-header-text"> Release sales for {retailer.name}</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      <br />
      <br />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleReleaseAtlasSalesClick();
        }}
      >
        {releaseAtlasButtonText}
      </Button>
      <br />
      <br />
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadPublishIdDevClick();
          }}
        >
          {loadPublishIdDevButtonText}
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackPublishIdDev || null}
            onChange={({ target: { value } }) => {
              setRollbackPublishIdDev(value);
              setDisableRevertDevButton(false);
            }}
          >
            {publishIdsDev.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentPublishIdForDevText}</div>
        </div>
        <Button
          disabled={disableRevertDevButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertSalesToPublishIdDevClick();
          }}
        >
          {revertPublishIdButtonDevText}
        </Button>
      </div>
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadPublishIdProdClick();
          }}
        >
          {loadPublishIdProdButtonText}
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackPublishIdProd || null}
            onChange={({ target: { value } }) => {
              setRollbackPublishIdProd(value);
              setDisableRevertProdButton(false);
            }}
          >
            {publishIdsProd.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentPublishIdForProdText}</div>
        </div>
        <Button
          disabled={disableRevertProdButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertSalesToPublishIdProdClick();
          }}
        >
          {revertPublishIdButtonProdText}
        </Button>
      </div>
      <br />
      <h4 className="sl-header-text">Doris Publishing Control</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      {dorisPublishingLoading ? (
        <div>Loading publishing status...</div>
      ) : (
        <div style={{ padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={dorisPublishingPaused}
                  onChange={handleDorisPublishingToggle}
                  disabled={dorisPublishingToggling}
                  color="warning"
                />
              }
              label={dorisPublishingPaused ? 'Publishing is PAUSED' : 'Publishing is ACTIVE'}
            />
            {dorisPublishingToggling && <span>Updating...</span>}
          </div>
          <div
            style={{
              marginTop: '8px',
              padding: '8px 12px',
              borderRadius: '4px',
              backgroundColor: dorisPublishingPaused ? '#fff3e0' : '#e8f5e9',
              border: `1px solid ${dorisPublishingPaused ? '#ff9800' : '#4caf50'}`,
              fontSize: '13px'
            }}
          >
            <strong>Status:</strong> {dorisPublishingPaused ? 'Paused' : 'Active'}
            {dorisPublishingUpdatedBy && (
              <span>
                {' '}| <strong>Last updated by:</strong> {dorisPublishingUpdatedBy}
              </span>
            )}
            {dorisPublishingUpdatedAt > 0 && (
              <span>
                {' '}| <strong>At:</strong> {new Date(dorisPublishingUpdatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
      <br />
      <h4 className="sl-header-text">Release Doris Atlas Sales Data for {retailer.name}</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      <br />
      <Button
        className={buttonClassName}
        onClick={async () => {
          handleReleaseDorisAtlasSalesDataClick();
        }}
      >
        {releaseDorisButtonText}
      </Button>
      <br />
      <br />
      <h4 className="sl-header-text">Doris Atlas Sales Publish IDs for {retailer.name}</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadDorisPublishIdDevClick();
          }}
        >
          {loadDorisPublishIdDevButtonText}
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackDorisPublishIdDev || null}
            onChange={({ target: { value } }) => {
              setRollbackDorisPublishIdDev(value);
              setDisableRevertDorisDevButton(false);
            }}
          >
            {dorisPublishIdsDev.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentDorisPublishIdForDevText}</div>
        </div>
        <Button
          disabled={disableRevertDorisDevButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertDorisToPublishIdDevClick();
          }}
        >
          {revertDorisPublishIdButtonDevText}
        </Button>
      </div>
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadDorisPublishIdProdClick();
          }}
        >
          {loadDorisPublishIdProdButtonText}
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackDorisPublishIdProd || null}
            onChange={({ target: { value } }) => {
              setRollbackDorisPublishIdProd(value);
              setDisableRevertDorisProdButton(false);
            }}
          >
            {dorisPublishIdsProd.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentDorisPublishIdForProdText}</div>
        </div>
        <Button
          disabled={disableRevertDorisProdButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertDorisToPublishIdProdClick();
          }}
        >
          {revertDorisPublishIdButtonProdText}
        </Button>
      </div>
      <br />
      <br />
      <h4 className="sl-header-text">Doris Atlas Promotions Publish IDs for {retailer.name}</h4>
      <hr className="sl-divider sl-divider--no-margin-top" />
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadDorisPublishIdDevClick();
          }}
        >
          Load Doris Dev Promotions IDs
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackDorisPromotionsPublishIdDev || null}
            onChange={({ target: { value } }) => {
              setRollbackDorisPromotionsPublishIdDev(value);
              setDisableRevertDorisPromotionsDevButton(false);
            }}
          >
            {dorisPromotionsPublishIdsDev.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentDorisPromotionsPublishIdForDevText}</div>
        </div>
        <Button
          disabled={disableRevertDorisPromotionsDevButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertDorisPromotionsToPublishIdDevClick();
          }}
        >
          {revertDorisPromotionsPublishIdButtonDevText}
        </Button>
      </div>
      <div style={{ display: 'flex' }}>
        <Button
          className={buttonClassName}
          onClick={async () => {
            handleLoadDorisPublishIdProdClick();
          }}
        >
          Load Doris Prod Promotions IDs
        </Button>
        <div style={{ width: '300px' }}>
          <Select
            variant="standard"
            style={{ border: '1px solid black' }}
            value={rollbackDorisPromotionsPublishIdProd || null}
            onChange={({ target: { value } }) => {
              setRollbackDorisPromotionsPublishIdProd(value);
              setDisableRevertDorisPromotionsProdButton(false);
            }}
          >
            {dorisPromotionsPublishIdsProd.map((publishId) => (
              <MenuItem value={publishId} key={publishId} style={{ width: '100%' }}>
                {publishId}
              </MenuItem>
            ))}
          </Select>
          <br />
          <div style={{ marginLeft: '20px' }}>{currentDorisPromotionsPublishIdForProdText}</div>
        </div>
        <Button
          disabled={disableRevertDorisPromotionsProdButton}
          className={buttonClassName}
          onClick={async () => {
            handleRevertDorisPromotionsToPublishIdProdClick();
          }}
        >
          {revertDorisPromotionsPublishIdButtonProdText}
        </Button>
      </div>
      <br />
      <br />
      <br />
      <br />
    </div>
  );
}

StacklineDataAdmin.propTypes = propTypes;

export default StacklineDataAdmin;
